import isEqual from 'lodash/isEqual';
import kebabCase from 'lodash/kebabCase';
import snakeCase from 'lodash/snakeCase';
import type {SchedulerTemplateForm} from 'src/types/scheduler/template-form';
import type {LocationMeta, Rule} from 'src/types/scheduler/templates';

import {
  SCHEDULER_MEMBER_MEETINGS_TABLE_CONTEXT,
  SCHEDULER_STAT_CARD_TYPE,
  type SchedulerMeetingsDrilldownRouteType,
} from 'src/components/scheduler/analytics/scheduler-amplitude-constants';
import {TEMPLATE_FORM_PUT_BODY_OMIT_KEYS} from 'src/components/scheduler/template-editor/constants';


const DRILLDOWN_ROUTE_TO_STAT_KEY: Record<
  SchedulerMeetingsDrilldownRouteType,
  keyof typeof SCHEDULER_STAT_CARD_TYPE
> = {
  'meetings-booked': 'MEETINGS_BOOKED',
  'meetings-conducted': 'MEETINGS_CONDUCTED',
  'meetings-cancelled': 'MEETINGS_CANCELLED',
};

//Dashboard `user_role`: scheduler admin vs everyone else with dashboard access.
export function getSchedulerDashboardUserRoleLabel(
  isAdmin: boolean,
): 'Admin' | 'Manager' {
  return isAdmin ? 'Admin' : 'Manager';
}

export function getMeetingsDrilldownExportTableContext(
  drilldownType: SchedulerMeetingsDrilldownRouteType,
  isFromTeamMemberDrilldown: boolean,
): string {
  if (isFromTeamMemberDrilldown) {
    return SCHEDULER_MEMBER_MEETINGS_TABLE_CONTEXT;
  }
  return SCHEDULER_STAT_CARD_TYPE[DRILLDOWN_ROUTE_TO_STAT_KEY[drilldownType]];
}

export type TemplateCreatedAmplitudeProps = {
  template_type: string;
  meeting_location_type: string;
  visibility: string;
  booking_flow_type: 'automatic' | 'manual';
  custom_questions_count: number;
  notifications_count: number;
  interviewers_count: number;
  has_conflict_prevention_rules: boolean;
  has_extended_availability: boolean;
  sequence_days_count: number;
};

const SCHEDULER_DISTRIBUTION_RULE_TO_LABEL: Record<string, string> = {
  single_account: '1:1 Individual',
  one_slot_multiple_event: 'Multi-Event',
  panel_interview: 'Panel',
  round_robin_availability: 'Round Robin Availability',
  round_robin_fairness: 'Round Robin Fairness',
  sequential_interview: 'Sequential',
};

export function templatesPageLabel(isTeamTemplate: boolean): string {
  return isTeamTemplate ? 'Team Templates' : 'Templates';
}

const CONFLICT_PREVENTION_RULE_TYPES: ReadonlySet<Rule['ruleType']> = new Set([
  'no_availability_rule',
  'conflict_prevention_date_based',
  'conflict_prevention_event_based',
  'conflict_prevention_event_outside_sense',
]);

const TEMPLATE_FORM_PUT_OMIT_KEY_SET: ReadonlySet<string> = new Set(
  TEMPLATE_FORM_PUT_BODY_OMIT_KEYS,
);

const LOCATION_TYPE_TO_LABEL: Record<
  NonNullable<LocationMeta['type']>,
  string
> = {
  custom_location: 'Custom',
  meeting_link: 'Video',
  meeting_room: 'In-person',
  phone_number: 'Phone Call',
};

function getMeetingLocationTypeLabel(
  locationMeta: ReadonlyArray<{type?: LocationMeta['type']}>,
): string {
  const type = locationMeta.find((meta) => meta.type)?.type;
  return type ? LOCATION_TYPE_TO_LABEL[type] ?? '' : '';
}

function getVisibilityLabel(rules: Rule[]): string {
  const visibility = rules.find((rule) => rule.ruleType === 'visibility_rule');
  return visibility?.value === 'private' ? 'Private' : 'Public';
}

function countConfiguredNotifications(form: SchedulerTemplateForm): number {
  let count = form.eventReminders?.length ?? 0;
  const confirmationNotifications = form.eventConfirmationNotifications;
  if (!confirmationNotifications) {
    return count;
  }
  count += confirmationNotifications.cancelEvent?.length ?? 0;
  count += confirmationNotifications.scheduleEvent?.length ?? 0;
  count += confirmationNotifications.rescheduleEvent?.length ?? 0;
  count += confirmationNotifications.requestRescheduleEvent?.length ?? 0;
  return count;
}

function hasConflictPreventionRules(rules: Rule[]): boolean {
  return rules.some((rule) =>
    CONFLICT_PREVENTION_RULE_TYPES.has(rule.ruleType),
  );
}

function getSequenceDaysCount(form: SchedulerTemplateForm): number {
  if (form.distributionRule !== 'sequential_interview') {
    return 0;
  }
  const sequence = (
    form.metadata as {sequence?: Array<{type?: string}>} | undefined
  )?.sequence;
  if (!Array.isArray(sequence)) {
    return 0;
  }
  return sequence.filter((sequenceItem) => sequenceItem.type === 'day').length;
}

function hasExtendedAvailability(form: SchedulerTemplateForm): boolean {
  const extendedLimit = form.extendedFutureLimit;
  return extendedLimit != null && extendedLimit.amount > 0;
}

/** Human-readable template type for Amplitude (matches spec display names). */
export function getSchedulerAmplitudeTemplateTypeLabel(
  distributionRule: string,
): string {
  return (
    SCHEDULER_DISTRIBUTION_RULE_TO_LABEL[distributionRule] ?? distributionRule
  );
}

/** Payload properties for `Template Created` (snake_case per spec). */
export function buildTemplateCreatedAmplitudeProps(
  form: SchedulerTemplateForm,
): TemplateCreatedAmplitudeProps {
  return {
    template_type: getSchedulerAmplitudeTemplateTypeLabel(
      form.distributionRule,
    ),
    meeting_location_type: getMeetingLocationTypeLabel(form.locationMeta ?? []),
    visibility: getVisibilityLabel(form.rules ?? []),
    booking_flow_type: form.requireBookingConfirmation ? 'manual' : 'automatic',
    custom_questions_count: form.additionalQuestions?.length ?? 0,
    notifications_count: countConfiguredNotifications(form),
    interviewers_count: form.bookingAccounts?.length ?? 0,
    has_conflict_prevention_rules: hasConflictPreventionRules(form.rules ?? []),
    has_extended_availability: hasExtendedAvailability(form),
    sequence_days_count: getSequenceDaysCount(form),
  };
}

function isUrlSuffixDerivedFromName(form: SchedulerTemplateForm): boolean {
  return form.urlSuffix === kebabCase(form.name ?? '');
}

export function getTemplateUpdatedFieldsChanged(
  before: SchedulerTemplateForm,
  after: SchedulerTemplateForm,
): string {
  const beforeRecord = before as unknown as Record<string, unknown>;
  const afterRecord = after as unknown as Record<string, unknown>;
  const changed: string[] = [];
  for (const key of Object.keys(beforeRecord)) {
    if (TEMPLATE_FORM_PUT_OMIT_KEY_SET.has(key)) {
      continue;
    }
    if (
      key === 'urlSuffix' &&
      isUrlSuffixDerivedFromName(before) &&
      isUrlSuffixDerivedFromName(after)
    ) {
      continue;
    }
    if (!isEqual(beforeRecord[key], afterRecord[key])) {
      changed.push(snakeCase(key));
    }
  }
  return changed.sort().join(',');
}
